import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import {
  ADVERT_IMAGE,
  ADVERT_STATUS_LABELS,
  AIRPORT,
  BOARD_SIZES,
  PLOT_IMAGE,
  RIVER_PLOTS,
  advertPrice,
  boardName,
  boardOf,
  formatPence,
  isPlots,
  plotCells,
  weekPriceOf,
  type AdvertOnAir,
  type AdvertsReply,
  type MyAdvert,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { ApiPicture } from '../lib/api-picture';
import { actionKey } from '../lib/format';

type View = 'board' | 'book' | 'mine';
type Props = {
  // the board tapped on the map (null: closed)
  board: number | null;
  onClose: () => void;
  adverts: AdvertsReply | null;
  // after a booking or a refund: the boards and the wallet look again
  onChanged: () => void;
  // signed in with a character: may book (otherwise this starts one)
  canBook: boolean;
  walletPence?: number;
  onStart: () => void;
};

const domainOf = (link: string) => {
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return link;
  }
};
const dateOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });

// the picture a player chose, fitted whole (never cropped) inside 1024 × 512 (a river plot: 512 × 512) on the
// screen's dark background and sent as a JPEG; smaller again if it is still too big
async function prepare(file: File, frame: { width: number; height: number }): Promise<string> {
  const image = await createImageBitmap(file);
  const scale = Math.min(1, frame.width / image.width, frame.height / image.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext('2d')!;
  context.fillStyle = '#0e1830';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close();
  for (const quality of [0.86, 0.72, 0.58]) {
    const data = canvas.toDataURL('image/jpeg', quality);
    if ((data.length - data.indexOf(',') - 1) * 0.75 <= ADVERT_IMAGE.maxBytes) return data;
  }
  throw new Error('That picture is too detailed to send. Try a simpler one.');
}

// A board on the map, as in Lagos Life's billboard sheet: what is on air now (each ad takes its turn), "Add your
// ad to this board" (picture, name, link, how long, pay), and "Your ads" with how each one stands. The river
// plots (Lagos Life's Sea Plots) open here too: the grid of plots, who holds which, and buying a square of them.
export function BillboardSheet({ board, onClose, adverts, onChanged, canBook, walletPence, onStart }: Props) {
  const [view, setView] = useState<View>('board');
  const [mine, setMine] = useState<MyAdvert[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // the booking
  const [picture, setPicture] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [days, setDays] = useState<number | null>(null);
  // the river plots: the size of square and its top left plot, or a held plot looked at
  const [span, setSpan] = useState<number>(1);
  const [cell, setCell] = useState<number | null>(null);
  const [looking, setLooking] = useState<AdvertOnAir | null>(null);
  const attempt = useRef(actionKey('advert'));
  const open = board !== null;
  useEffect(() => {
    if (!open) return;
    setView('board');
    setFailure(null);
    setPicture(null);
    setTitle('');
    setLink('');
    setDays(null);
    setSpan(1);
    setCell(null);
    setLooking(null);
    attempt.current = actionKey('advert');
  }, [open, board]);
  const loadMine = useCallback(async () => {
    try {
      setMine((await api.adverts.mine()).adverts);
    } catch (error) {
      setFailure(describeError(error).message);
    }
  }, []);
  useEffect(() => {
    if (open && canBook) void loadMine();
  }, [open, canBook, loadMine]);

  const settings = adverts?.settings ?? null;
  const plots = board !== null && isPlots(board);
  const spot = plots && cell !== null ? { cell, span } : null;
  const shape = plots ? { w: 1, h: 1 } : BOARD_SIZES[board ? (boardOf(board)?.size ?? 'medium') : 'medium'];
  const where = board ? boardOf(board)?.where : null;
  const name = board ? boardName(board, spot) : 'Board';
  // the plots no one else can buy now
  const held = useMemo(
    () => new Set((adverts?.plots ?? []).flatMap((item) => plotCells(item.cell, item.span))),
    [adverts],
  );
  const onAir: AdvertOnAir[] = useMemo(
    () => (adverts?.onAir ?? []).filter((advert) => advert.board === board),
    [adverts, board],
  );
  const full = Boolean(board && settings && (adverts?.taken[String(board)] ?? 0) >= settings.perBoard);
  const run = days ?? settings?.days[0] ?? 7;
  const price = settings && board ? advertPrice(settings, board, run, span) : 0;
  const weekly = settings && board ? weekPriceOf(settings, board, span) : 0;
  // with "a player's first ad is checked", one that has been live before goes straight up
  const checked =
    settings?.approval === 'all' || (settings?.approval === 'first' && !(mine ?? []).some((advert) => advert.startsAt));
  const short = walletPence !== undefined && walletPence < price;

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setFailure(null);
    try {
      if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error('Choose a JPG, PNG or WebP picture.');
      setPicture(await prepare(file, plots ? PLOT_IMAGE : ADVERT_IMAGE));
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'That picture could not be read.');
    }
  };
  const book = async () => {
    if (!board || !picture) return;
    setBusy(true);
    setFailure(null);
    try {
      const reply = await api.adverts.book(
        {
          board,
          cell: spot?.cell ?? null,
          span: spot?.span ?? null,
          days: run,
          title: title.trim(),
          link: link.trim() || null,
          image: picture,
        },
        attempt.current,
      );
      attempt.current = actionKey('advert');
      notify(
        reply.advert.status === 'live'
          ? 'Payment received · your ad is live! 📢'
          : 'Booked! Our team will check your ad and put it up.',
      );
      onChanged();
      await loadMine();
      setView('mine');
    } catch (error) {
      setFailure(describeError(error).message);
      // someone took the plots first: the grid looks again
      if (plots) onChanged();
    } finally {
      setBusy(false);
    }
  };
  // pick the top left plot of a square (moved in so it fits on the river)
  const pick = (index: number) => {
    const column = Math.min(index % RIVER_PLOTS.columns, RIVER_PLOTS.columns - span);
    const row = Math.min(Math.floor(index / RIVER_PLOTS.columns), RIVER_PLOTS.rows - span);
    const top = row * RIVER_PLOTS.columns + column;
    setLooking(null);
    setFailure(null);
    if (plotCells(top, span).some((item) => held.has(item))) {
      setCell(null);
      setFailure('Some of those plots are taken. Pick another spot.');
    } else setCell(top);
  };
  const withdraw = async (advert: MyAdvert) => {
    setBusy(true);
    setFailure(null);
    try {
      setMine((await api.adverts.withdraw(advert.id)).adverts);
      notify(`Booking cancelled · ${formatPence(advert.pricePence)} refunded`);
      onChanged();
    } catch (error) {
      setFailure(describeError(error).message);
    } finally {
      setBusy(false);
    }
  };

  const back = (
    <button type="button" className="neighbours-back" onClick={() => setView('board')}>
      <ChevronLeft size={16} /> {board ? boardName(board) : 'Board'}
    </button>
  );
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone billboard-sheet">
        {view === 'book' ? (
          <>
            {back}
            <DialogTitle>Add your ad</DialogTitle>
            <DialogDescription>
              {name} · {formatPence(weekly)} per 7 days
            </DialogDescription>
            <div className="advert-step">
              <strong>1 · Your picture</strong>
              <div
                className="advert-preview"
                style={{ aspectRatio: `${shape.w} / ${shape.h}` }}
                aria-label={picture ? 'Your picture on the board' : undefined}
              >
                {picture ? (
                  <img src={picture} alt="Your ad" />
                ) : (
                  <span>{plots ? 'A plot is square' : 'The board is twice as wide as it is tall'}</span>
                )}
              </div>
              <label className="advert-file">
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => void choose(event)} />
                {picture ? 'Change picture' : 'Choose a picture'}
              </label>
              <small>
                Your logo or ad (JPG, PNG or WebP). It is fitted to the {plots ? 'plot' : 'board'}, never cropped.
              </small>
            </div>
            <div className="advert-step">
              <strong>2 · Your details</strong>
              <label className="advert-field">
                <span>Business or ad name</span>
                <input value={title} maxLength={40} onChange={(event) => setTitle(event.target.value)} />
              </label>
              <label className="advert-field">
                <span>Link players go to (optional)</span>
                <input
                  value={link}
                  inputMode="url"
                  placeholder="https://"
                  maxLength={300}
                  onChange={(event) => setLink(event.target.value)}
                />
              </label>
            </div>
            <div className="advert-step">
              <strong>3 · How long</strong>
              <div className="advert-days" role="radiogroup" aria-label="How long">
                {(settings?.days ?? []).map((choice) => (
                  <button
                    type="button"
                    key={choice}
                    role="radio"
                    aria-checked={run === choice}
                    className={run === choice ? 'active' : ''}
                    onClick={() => setDays(choice)}
                  >
                    <strong>
                      {choice} {choice === 1 ? 'day' : 'days'}
                    </strong>
                    <small>{settings && board ? formatPence(advertPrice(settings, board, choice, span)) : ''}</small>
                  </button>
                ))}
              </div>
            </div>
            <p className="neighbours-note">
              {checked
                ? 'Our team checks it before it goes live. If it is not approved, you get your money back.'
                : 'It goes live as soon as you pay.'}{' '}
              Scams, adult content, coins or tokens, selling accounts, or pretending to be London Life are taken down
              without a refund.
            </p>
            {failure && (
              <p className="advert-failure" role="alert">
                {failure}
              </p>
            )}
            <button
              type="button"
              className="city-card-go"
              disabled={busy || !picture || title.trim().length < 2 || short}
              onClick={() => void book()}
            >
              {short ? `You need ${formatPence(price)}` : `Pay ${formatPence(price)}`}
            </button>
          </>
        ) : view === 'mine' ? (
          <>
            {back}
            <DialogTitle>Your ads</DialogTitle>
            <DialogDescription>Every board you have booked, and how each ad stands.</DialogDescription>
            {failure && (
              <p className="advert-failure" role="alert">
                {failure}
              </p>
            )}
            {!mine ? (
              <p className="neighbours-note">Looking…</p>
            ) : !mine.length ? (
              <p className="neighbours-note">You haven&rsquo;t booked a board yet.</p>
            ) : (
              <ul className="advert-mine">
                {mine.map((advert) => (
                  <li key={advert.id}>
                    <span className="advert-thumb">
                      <ApiPicture path={advert.image} alt={advert.title} />
                    </span>
                    <span className="advert-who">
                      <strong>{advert.title}</strong>
                      <small>
                        {boardName(advert.board, advert)} · {advert.days} {advert.days === 1 ? 'day' : 'days'} ·{' '}
                        {formatPence(advert.pricePence)}
                      </small>
                      <em className={`advert-status ${advert.status}`}>
                        {advert.status === 'live' && advert.endsAt
                          ? `Live until ${dateOf(advert.endsAt)}`
                          : ADVERT_STATUS_LABELS[advert.status]}
                      </em>
                      {advert.reason && <small>{advert.reason}</small>}
                    </span>
                    {advert.status === 'pending' && (
                      <button type="button" disabled={busy} onClick={() => void withdraw(advert)}>
                        Cancel
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : plots ? (
          <>
            <DialogTitle>🌊 River plots</DialogTitle>
            <DialogDescription>
              Square plots on the Thames at Woolwich Reach · one ad per plot, seen by every player
            </DialogDescription>
            {canBook && settings?.open && (
              <div className="advert-days" role="radiogroup" aria-label="Plot size">
                {RIVER_PLOTS.spans.map((choice) => (
                  <button
                    type="button"
                    key={choice}
                    role="radio"
                    aria-checked={span === choice}
                    className={span === choice ? 'active' : ''}
                    onClick={() => {
                      setSpan(choice);
                      setCell(null);
                      setFailure(null);
                    }}
                  >
                    <strong>
                      {choice}×{choice}
                    </strong>
                    <small>
                      {settings ? formatPence(weekPriceOf(settings, RIVER_PLOTS.board, choice)) : ''} / 7 days
                    </small>
                  </button>
                ))}
              </div>
            )}
            <div className="river-plots-scroll">
              <div
                className="river-plots"
                role="grid"
                aria-label="River plots"
                style={{ gridTemplateColumns: `repeat(${RIVER_PLOTS.columns}, var(--plot))` }}
              >
                {Array.from({ length: RIVER_PLOTS.columns * RIVER_PLOTS.rows }, (_, index) =>
                  held.has(index) ? null : (
                    <button
                      type="button"
                      key={index}
                      className="river-plot free"
                      style={{
                        gridColumn: (index % RIVER_PLOTS.columns) + 1,
                        gridRow: Math.floor(index / RIVER_PLOTS.columns) + 1,
                      }}
                      aria-label={`${boardName(RIVER_PLOTS.board, { cell: index, span: 1 })}: free`}
                      disabled={!canBook || !settings?.open}
                      onClick={() => pick(index)}
                    />
                  ),
                )}
                {(adverts?.plots ?? []).map((item) => {
                  const ad = onAir.find((advert) => advert.cell === item.cell);
                  const place = {
                    gridColumn: `${(item.cell % RIVER_PLOTS.columns) + 1} / span ${item.span}`,
                    gridRow: `${Math.floor(item.cell / RIVER_PLOTS.columns) + 1} / span ${item.span}`,
                  };
                  return ad ? (
                    <button
                      type="button"
                      key={`held-${item.cell}`}
                      className={`river-plot taken${looking?.id === ad.id ? ' looking' : ''}`}
                      style={place}
                      aria-label={`${boardName(RIVER_PLOTS.board, item)}: ${ad.title}`}
                      onClick={() => {
                        setCell(null);
                        setFailure(null);
                        setLooking(ad);
                      }}
                    >
                      <ApiPicture path={ad.image} alt="" />
                    </button>
                  ) : (
                    <span
                      key={`held-${item.cell}`}
                      className="river-plot sold"
                      style={place}
                      aria-label={`${boardName(RIVER_PLOTS.board, item)}: sold`}
                    >
                      SOLD
                    </span>
                  );
                })}
                {spot && (
                  <span
                    className="river-plot chosen"
                    aria-hidden="true"
                    style={{
                      gridColumn: `${(spot.cell % RIVER_PLOTS.columns) + 1} / span ${spot.span}`,
                      gridRow: `${Math.floor(spot.cell / RIVER_PLOTS.columns) + 1} / span ${spot.span}`,
                    }}
                  />
                )}
              </div>
            </div>
            {looking ? (
              <div className="river-plot-info">
                <strong>{looking.title}</strong>
                <small>{boardName(RIVER_PLOTS.board, looking)}</small>
                {looking.link && (
                  <a href={looking.link} target="_blank" rel="noopener noreferrer nofollow ugc">
                    Visit {domainOf(looking.link)} <ExternalLink size={13} />
                  </a>
                )}
              </div>
            ) : (
              <p className="neighbours-note">
                {onAir.length
                  ? `${onAir.length} ${onAir.length === 1 ? 'ad' : 'ads'} on the river. Tap one to see it.`
                  : 'No one is on the river yet. Yours could be the first.'}{' '}
                {`${RIVER_PLOTS.columns} × ${RIVER_PLOTS.rows} plots: swipe the river to see them all.`}
              </p>
            )}
            {failure && (
              <p className="advert-failure" role="alert">
                {failure}
              </p>
            )}
            {!canBook ? (
              <button type="button" className="city-card-go" onClick={onStart}>
                Create your character to advertise
              </button>
            ) : !settings?.open ? (
              <p className="neighbours-note">Buying plots is paused for now.</p>
            ) : (
              <button type="button" className="city-card-go" disabled={!spot} onClick={() => setView('book')}>
                {spot ? `Buy ${name} · ${formatPence(weekly)} / 7 days` : 'Tap a free plot to choose it'}
              </button>
            )}
            {canBook && (
              <button type="button" className="advert-mine-open" onClick={() => setView('mine')}>
                Your ads{mine?.length ? ` (${mine.length})` : ''}
              </button>
            )}
          </>
        ) : (
          <>
            <DialogTitle>
              {where === 'airport' ? '✈️' : '📢'} {board ? boardName(board) : 'Board'}
            </DialogTitle>
            <DialogDescription>
              {where === 'airport'
                ? `A digital board at ${AIRPORT}, every flyer passes it`
                : 'A digital board at the top of the map'}{' '}
              · ads take turns, {settings?.rotateSeconds ?? 15} seconds each
            </DialogDescription>
            <strong className="advert-on-air">● On air</strong>
            {onAir.length ? (
              <ul className="advert-carousel" aria-label="On air">
                {onAir.map((advert) => (
                  <li key={advert.id}>
                    <span className="advert-picture" style={{ aspectRatio: `${shape.w} / ${shape.h}` }}>
                      <ApiPicture path={advert.image} alt={advert.title} />
                    </span>
                    <strong>{advert.title}</strong>
                    {advert.link && (
                      <a href={advert.link} target="_blank" rel="noopener noreferrer nofollow ugc">
                        Visit {domainOf(advert.link)} <ExternalLink size={13} />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="neighbours-note">Nothing on this board yet. Yours could be the first.</p>
            )}
            {!canBook ? (
              <button type="button" className="city-card-go" onClick={onStart}>
                Create your character to advertise
              </button>
            ) : !settings?.open ? (
              <p className="neighbours-note">Booking the boards is paused for now.</p>
            ) : full ? (
              <p className="neighbours-note">This board is full right now. Try another board.</p>
            ) : (
              <button type="button" className="city-card-go" onClick={() => setView('book')}>
                + Add your ad to this board · {formatPence(weekly)} / 7 days
              </button>
            )}
            {canBook && (
              <button type="button" className="advert-mine-open" onClick={() => setView('mine')}>
                Your ads{mine?.length ? ` (${mine.length})` : ''}
              </button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
